import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { NgStyle, NgTemplateOutlet } from '@angular/common';
import { DuelTierDataWithMeta } from '../../../../core/services/tiers/tiers.service';
import { TournamentDataModel } from '../../../../core/services/tournament/model/tournament-data-model';
import { WaitingOpponentsData } from '../../../../core/api/player/schemas/waiting-opponents';
import { ThemeService } from '../../../../core/services/theme/theme.service';
import { OperatorService } from '../../../../core/services/operator/operator.service';
import { ThemePipe } from '../../../../core/pipes/theme.pipe';
import { TournamentInfoCardComponent } from '../../../tournament/tournament-info-card/tournament-info-card.component';
import { PracticeCardComponent } from '../practice-card/practice-card.component';
import { WagerCardComponent } from '../wager-card/wager-card.component';
import { IconComponent } from '../../../icon/icon.component';
import { environment } from '../../../../../environments/environment';
import { OperatorId } from '../../../../core/services/operator/models/operator-id';
import {
  DuelPaymentEventData,
  TournamentPaymentEventData,
} from 'src/app/shared/types/payment-events';

@Component({
  selector: 'app-wager-tier-list',
  templateUrl: './wager-tier-list.component.html',
  styleUrls: ['./wager-tier-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgStyle,
    TranslocoDirective,
    TournamentInfoCardComponent,
    PracticeCardComponent,
    NgTemplateOutlet,
    WagerCardComponent,
    IconComponent,
    ThemePipe,
  ],
})
export class WagerTierListComponent implements OnInit, OnDestroy {
  @Input() isInDrawer = false;
  @Input({ required: true }) allTiers!: Signal<DuelTierDataWithMeta[]>;
  @Input({ required: true }) currentTournamentData!: Signal<TournamentDataModel | undefined>;
  @Input({ required: true }) waitingOpponentsStandard!: Signal<WaitingOpponentsData | undefined>;

  @Output() wagerClickEvent = new EventEmitter<DuelPaymentEventData>();
  @Output() tournamentPlayClickEvent = new EventEmitter<TournamentPaymentEventData>();
  @Output() practiceClickEvent = new EventEmitter<void>();
  @Output() localClickEvent = new EventEmitter<void>();

  private readonly currentTime: WritableSignal<Date> = signal(new Date());
  private tickIntervalId: ReturnType<typeof setInterval> | null = null;

  protected readonly operatorService = inject(OperatorService);
  protected readonly themeService = inject(ThemeService);

  readonly unlockCelebrationActive = signal(false);

  readonly titleStyle: [string, string][] = [
    ['type', 'panel-wager-tier-list-title'],
    ['text-color', 'panel-wager-tier-list-title'],
  ];

  readonly bodyStyle: [string, string][] = [
    ['type', 'panel-wager-tier-list-body'],
    ['text-color', 'panel-wager-tier-list-body'],
  ];

  readonly playerWaitingStyle: [string, string][] = [
    ['type', 'panel-wager-tier-list-footer'],
    ['text-color', 'panel-wager-tier-list-footer'],
  ];

  readonly tick = this.currentTime.asReadonly();
  readonly isPracticeFirst = computed(() => this.themeService.wagerDrawerConfig().practiceFirst);
  readonly hidePractice = computed(() => this.themeService.wagerDrawerConfig().hidePractice);
  readonly canShowLocal =
    environment.target !== 'production' &&
    (this.operatorService.operatorId === OperatorId.thndr ||
      this.operatorService.operatorId === OperatorId.fakebet);

  ngOnInit(): void {
    this.tickIntervalId = setInterval(() => {
      this.currentTime.set(new Date());
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.tickIntervalId !== null) {
      clearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }
  }

  onPracticeClick(): void {
    this.practiceClickEvent.emit();
  }

  onLocalClick(): void {
    this.localClickEvent.emit();
  }

  onWagerClick(data: DuelPaymentEventData): void {
    this.wagerClickEvent.emit(data);
  }

  onTournamentPlayClick(data: TournamentPaymentEventData): void {
    this.tournamentPlayClickEvent.emit(data);
  }

  onUnlockCelebrationComplete(): void {
    this.unlockCelebrationActive.set(true);
    setTimeout(() => this.unlockCelebrationActive.set(false), 0);
  }
}
